import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, CircularProgress, Chip } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon, Error as ErrorIcon } from '@mui/icons-material';
import { compareWithDatabase } from '../../../../services/fundHistoryService';

const DataIntegrityChecker = () => {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    setChecking(true);
    setResult(null);

    try {
      const response = await compareWithDatabase();
      setResult(response);
    } catch (error) {
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <Box>
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#fff' }}>
              Data Integrity Check
            </Typography>
            
            <Button
              variant="contained"
              onClick={handleCheck}
              disabled={checking}
              startIcon={checking ? <CircularProgress size={20} /> : null}
              sx={{
                background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
                  opacity: 0.9
                },
                '&:disabled': {
                  background: '#555',
                  color: '#aaa'
                }
              }}
            >
              {checking ? 'Checking...' : 'Run Integrity Check'}
            </Button>
          </Box>

          <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 3 }}>
            Compare spreadsheet data with Rowy database to identify discrepancies
          </Typography>

          {result && (
            <Box sx={{ mt: 3 }}>
              {result.success ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {result.data.status === 'all_match' ? (
                      <>
                        <CheckCircleIcon sx={{ color: '#2af598', fontSize: '24px' }} />
                        <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#2af598' }}>
                          All Data Matches
                        </Typography>
                      </>
                    ) : (
                      <>
                        <WarningIcon sx={{ color: '#ffc107', fontSize: '24px' }} />
                        <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#ffc107' }}>
                          Discrepancies Found
                        </Typography>
                      </>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${result.data.totalMonthsChecked} Months Checked`}
                      sx={{
                        backgroundColor: 'rgba(42, 245, 152, 0.1)',
                        color: '#2af598',
                        border: '1px solid rgba(42, 245, 152, 0.3)'
                      }}
                    />
                    
                    {result.data.discrepanciesFound > 0 && (
                      <Chip
                        label={`${result.data.discrepanciesFound} Discrepancies`}
                        sx={{
                          backgroundColor: 'rgba(255, 193, 7, 0.1)',
                          color: '#ffc107',
                          border: '1px solid rgba(255, 193, 7, 0.3)'
                        }}
                      />
                    )}
                  </Box>

                  {result.data.discrepancies && result.data.discrepancies.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#fff', mb: 2 }}>
                        Issues Found:
                      </Typography>
                      
                      <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {result.data.discrepancies.map((disc, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              mb: 1,
                              backgroundColor: 'rgba(255, 193, 7, 0.05)',
                              border: '1px solid rgba(255, 193, 7, 0.2)',
                              borderRadius: '4px'
                            }}
                          >
                            <Typography sx={{ fontSize: '14px', color: '#fff', mb: 0.5 }}>
                              {disc.description}
                            </Typography>
                            {disc.difference && (
                              <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
                                Difference: ${disc.difference.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon sx={{ color: '#f52a2d', fontSize: '24px' }} />
                  <Typography sx={{ fontSize: '14px', color: '#f52a2d' }}>
                    Error: {result.error}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {!result && !checking && (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                px: 2,
                border: '1px dashed rgba(255, 255, 255, 0.2)',
                borderRadius: '4px'
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#aaa' }}>
                Click "Run Integrity Check" to compare data
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#2af598'
            }}
          />
          <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
            Match (difference &lt; $10)
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#ffc107'
            }}
          />
          <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
            Minor Discrepancy ($10-$100)
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#f52a2d'
            }}
          />
          <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
            Major Discrepancy (&gt; $100)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DataIntegrityChecker;

